import React from "react";
import UseCaseTemplate from "./UseCaseTemplate";
import museum1 from "./assets/musee/musee1_1.jpg";
import museum1b from "./assets/musee/musee1_2.jpg";
import museum2 from "./assets/musee/musee2.jpg";
import museum3 from "./assets/musee/musee3.jpg";
import museum4 from "./assets/musee/musee4.jpg";
import museum5 from "./assets/musee/musee5.jpg";
import museum6 from "./assets/musee/musee6.jpg";
import museum7 from "./assets/musee/musee7.jpg";

const experiences = [
  {
    id: "polyphonic-resonance",
    number: "01",
    category: "Origins & cultural dialogue",
    title: "Polyphonic Resonance & Dialogue of Origins",
    lead: "Surround the physical artifact with living expertise, authentic soundscapes and perspectives connected to its cultural origin.",
    problem: "Antiquities in a universal museum can be reduced to a brief static label written through a single curatorial lens; standard audio guides often lack living cultural context and authentic soundscapes.",
    layout: "Symphony",
    layoutDetail: "Acoustic footprint synchronization, ceremonial soundscapes and expert polyphony",
    workflowTitle: "A multi-voice artifact layer",
    workflow: [
      "Connect a museum curator with an active field archaeologist in Egypt.",
      "Add a local Egyptian heritage guide.",
      "Add an ethnomusicologist around the same physical statuette in the gallery.",
      "Synchronize their cultural and acoustic perspectives into one living media object.",
    ],
    outcome1Label: "Institutional impact",
    outcome1: "Supports cultural diplomacy and inclusive curation imperatives while the supplied concept targets visitor dwell-time gains above 300%.",
    outcome2Label: "Weaver monetization",
    outcome2: "Premium in-app access (€1–€2 in the supplied concept) can be distributed through Smart Escrow to independent researchers and local heritage guides according to verified viewing and engagement.",
    image: museum1,
  },
  {
    id: "radiographic-lens",
    number: "02",
    category: "Conservation science",
    title: "The Radiographic Lens & Restoration Secrets",
    lead: "Reveal invisible historical and conservation layers without adding bulky screens or dense text panels to the gallery.",
    problem: "Visitors cannot perceive pentimenti, preparatory sketches or past conservation work beneath the visible surface, and static text panels are difficult to read in dim galleries.",
    layout: "Versus",
    layoutDetail: "Synchronized 50/50 split-screen with interactive slider",
    workflowTitle: "Layer-by-layer inspection",
    workflow: [
      "Left side: ultra-high-resolution visible-light capture of the displayed masterpiece.",
      "Right side: infrared reflectography, X-ray radiography or UV fluorescence.",
      "Museum conservators and laboratory scientists upload 10-second micro-clips synchronized to specific structural details.",
    ],
    outcome1Label: "Institutional impact",
    outcome1: "Showcases cutting-edge scientific conservation without cluttering historic galleries with physical screens or kiosks.",
    outcome2Label: "Visitor value",
    outcome2: "Makes hidden sketches, corrected brushstrokes, wood grain and micro-restoration work visible in direct relation to the artwork.",
    image: museum2,
  },
  {
    id: "relay-civilizations",
    number: "03",
    category: "Cross-wing journeys",
    title: "The Relay of Civilizations",
    lead: "Build thematic journeys across distant departments without physically relocating the collection.",
    problem: "Large museums create gallery fatigue, while cross-departmental narratives cannot be exhibited side-by-side because relevant works are dispersed across different wings.",
    layout: "Relay",
    layoutDetail: "Kinetic visual transitions and spatial baton-passing",
    workflowTitle: "A thematic journey across the museum",
    workflow: [
      "A curator introduces the first work in one department.",
      "The narrative baton passes to a curator in another wing and another historical period.",
      "Transit micro-clips guide the visitor between locations.",
      "Relational AI maintains visual rhythm and harmonizes color grading across different gallery lighting conditions.",
    ],
    outcome1Label: "Institutional impact",
    outcome1: "Redistributes visitor crowds away from overcrowded masterworks and enables custom thematic tours without temporary construction costs.",
    outcome2Label: "Visitor value",
    outcome2: "Turns physically separated collections into one coherent thematic route that can be followed on a phone.",
    image: museum3,
  },
  {
    id: "traveling-exhibitions",
    number: "04",
    category: "Loans & temporary exhibitions",
    title: "International Loans & Traveling Exhibitions",
    lead: "Keep the digital exhibition catalog current even when guest curators are remote or loaned works rotate.",
    problem: "Guest curators cannot remain on-site for an entire international exhibition, and printed catalogs or static videos become obsolete when loaned pieces leave early or move to the next venue.",
    layout: "Storyboard",
    layoutDetail: "Modular chronological chapters with real-time hot-swapping",
    workflowTitle: "Remote partner contributions",
    workflow: [
      "Give each lending institution a dedicated Weaver slot.",
      "A guest curator records insight remotely from the lending museum.",
      "If a work leaves the exhibition, update its slot remotely in minutes.",
      "Keep the same gallery-wall QR codes and avoid app updates.",
    ],
    outcome1Label: "Institutional impact",
    outcome1: "Reduces audiovisual production costs for temporary exhibitions and deepens bilateral partnerships between cultural institutions.",
    outcome2Label: "Operational value",
    outcome2: "Partner institutions can update their contribution directly without rebuilding the full exhibition media package.",
    image: museum4,
  },
  {
    id: "citizen-voices",
    number: "05",
    category: "UGC & accessibility",
    title: "Citizen Voices & Inclusive Cultural Mediation",
    lead: "Bring younger audiences and diverse accessibility formats into the official museum media layer through curated community contributions.",
    problem: "Museums struggle to engage younger audiences and provide formats such as sign language, poetic audio description and youth-oriented explanation while social comments remain scattered and unvetted.",
    layout: "Seed",
    layoutDetail: "Prompt-driven distributed missions with automated AI compliance",
    workflowTitle: "Curated open missions",
    workflow: [
      "Launch a Seed Mission around a selected masterpiece.",
      "Reserve dedicated slots for fine-arts students and certified Sign Language interpreters.",
      "AI Content Guard validates visual and acoustic clarity, filters inappropriate submissions and blurs incidental visitors.",
      "Curators approve standout clips for the official QR-linked living feed.",
    ],
    outcome1Label: "Institutional impact",
    outcome1: "Democratizes cultural dialogue, turns passive visitors into cultural ambassadors and expands accessible interpretation layers.",
    outcome2Label: "Community value",
    outcome2: "Specialized creators and students gain institutional visibility inside the official interpretive experience.",
    image: museum5,
  },
  {
    id: "curatorial-grants",
    number: "06",
    category: "Scholarship & philanthropy",
    title: "Curatorial Challenges & Philanthropic Micro-Grants",
    lead: "Invite independent scholars, emerging creators and students to contribute structured educational insight to a museum-defined theme.",
    layout: "Seed",
    layoutDetail: "Open transversal blueprint with curator-selected slots",
    workflowTitle: "Funded educational callouts",
    workflow: [
      "Publish a transversal blueprint such as “Representations of Divine Power across Antiquity”.",
      "Invite scholars, digital creators and university students to submit 30-second analytical insights.",
      "Use AI verification for historical accuracy and visual compliance.",
      "Curators select the strongest entries for the official gallery stream.",
    ],
    outcome1Label: "Institutional impact",
    outcome1: "Extends curatorial programming through an open but governed contribution model funded by philanthropy or sponsorship.",
    outcome2Label: "Weaver monetization",
    outcome2: "A sponsor-funded escrow pool (€500–€2,500 in the supplied concept) can release micro-grants immediately to selected student Weavers with public institutional attribution.",
    image: museum6,
  },
  {
    id: "inclusive-marketplace",
    number: "07",
    category: "Accessibility marketplace",
    title: "The Inclusive Mediation Marketplace",
    lead: "Create a verified marketplace for specialized accessibility contributions around individual works.",
    layout: "Seed",
    layoutDetail: "Bounty missions plus direct visitor tipping",
    workflowTitle: "Accessibility contribution market",
    workflow: [
      "Post specific missions such as French Sign Language interpretation for a named artwork.",
      "A specialized accessibility creator uploads the contribution.",
      "AI Content Guard verifies visual clarity and the curator approves compliance.",
      "Visitors can reward an interpreter or volunteer educator directly inside the player.",
    ],
    outcome1Label: "Institutional impact",
    outcome1: "Expands high-quality accessibility content through a repeatable, governed mission model.",
    outcome2Label: "Weaver monetization",
    outcome2: "Escrowed mission rewards (for example €50 in the supplied concept) release on approval, while direct visitor tips can add €0.50–€2.00 per contribution.",
    image: museum7,
  },
  {
    id: "intangible-heritage",
    number: "08",
    category: "Living heritage",
    title: "Living Intangible Heritage",
    lead: "Reconnect ceremonial objects with living artists, musicians and tradition-bearers who still practice the associated performance and ritual.",
    layout: "Symphony",
    layoutDetail: "Artifact-linked performance and acoustic ritual layer",
    workflowTitle: "From object to living performance",
    workflow: [
      "Pair an inanimate ceremonial object with living performers connected to its tradition.",
      "Capture original hymns, chants and acoustic rituals connected to the artifact.",
      "Make the performance available to on-site and global online catalog audiences.",
    ],
    outcome1Label: "Institutional impact",
    outcome1: "Allows the museum to act as a digital patron of intangible cultural heritage rather than presenting only the inanimate object.",
    outcome2Label: "Weaver monetization",
    outcome2: "Streaming can generate micropayments, with Smart Escrow routing a guaranteed share of digital access revenue directly to source-community artists.",
    image: museum1b,
  },
];

export default function MuseumPage() {
  return (
    <UseCaseTemplate
      pageTitle={"Museum"}
      currentPath={"/use-cases/museum"}
      eyebrow={"WEAVEVO USE CASE · MUSEUM"}
      heroTitle={"Turn collections into living cultural experiences."}
      heroText={"Connect curators, scientists, local knowledge bearers, partner institutions and accessibility creators around the same artwork through evolving, multi-perspective media."}
      heroAlt={"Museum visitor using Weavevo around a physical collection object"}
      finalEyebrow={"BUILD THE NEXT MUSEUM WEAVE"}
      finalTitle={"The object stays in the gallery. Its story can keep growing."}
      finalText={"Layer scientific insight, cultural context, international expertise and inclusive mediation around the collection without adding physical clutter to the gallery."}
      heroImage={museum1}
      experiences={experiences}
    />
  );
}
